import { PureComponent, ReactNode } from 'react';
import PadV2AutoSuggest, {
    AllOptions,
    AS_OPTIONS_SEARCH,
    CustomDataForAS,
} from '../../../rightPanel/workspace/padV2/PadV2AutoSuggest';
import { batch, useDispatch, useSelector } from 'react-redux';
import { DentalExaminationIcon, DentalProcedureIcon } from '../../../../common/Icons';
import {
    DENTAL_ENTITY_TYPE,
    DentalExamination,
    Surface,
} from '../../../../../store/types/prescription';
import { getDentalExaminations } from '../../../../../store/selectors/prescriptionPad';
import { upsertDentalExamination } from '../../../../../store/actions/prescriptionPad';
import fetchMatchingSymDia, {
    SymDia,
    SymDiaResponse,
} from '../../../../../api/fetchMatchingSymDia';
import { generateLocalIdForEntity, randomString } from '../../../../../store/utils';
import { GDentalExamination, GDentalPastProcedure } from '../../../../../store/db';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { AS_OPTIONS_FREQUENCTLY } from '../../../trackSource';
import {
    DENTAL_EXAMINATION_AS_EL_ID,
    DENTAL_EXAMINATION_AS_INPUT,
    Group,
} from '../DentalExamination';
import { allSurfacesIdToName } from '../constants';
import fetchMatchingProcedures, {
    ProcedureSuggestions,
} from '../../../../../api/fetchMatchingProcedures';
import PadGenieAutoSuggestFuseManager, {
    DENTAL_EXAMINATIONS,
    DENTAL_PAST_PROCEDURES,
} from '../../../PadGenieAutoSuggestFuseManager';

type Props = {
    onSelect: (option: DentalExamination) => void;
    elements: ReadonlyArray<DentalExamination>;
    teeth: string;
    group?: Group;
};

const ExaminationOptionIcon = DentalExaminationIcon('w-12 h-12 fill-darwin-green-primary');
const ProcedureOptionIcon = DentalProcedureIcon('w-12 h-12 fill-darwin-accent-medicines-pink-800');

const customDataForAS: CustomDataForAS = {
    [DENTAL_ENTITY_TYPE.EXAMINATION]: {
        optionIconText: ExaminationOptionIcon,
        optionIconBgClass: 'circle-inventory-in-stock',
    },
    [DENTAL_ENTITY_TYPE.PROCEDURE]: {
        optionIconText: ProcedureOptionIcon,
        optionIconBgClass: 'circle-mx',
    },
};

class DentalExaminationAutoSuggest extends PureComponent<Props> {
    getOptions = (
        setOptions: (options: AllOptions<DentalExamination>, inputValue?: string) => void,
        input?: string,
    ): void => {
        const { elements } = this.props;
        const options: AllOptions<DentalExamination> = [];
        const gExaminationsFuse = PadGenieAutoSuggestFuseManager.getFuseForType(
            DENTAL_EXAMINATIONS,
        );
        const gPastProceduresFuse = PadGenieAutoSuggestFuseManager.getFuseForType(
            DENTAL_PAST_PROCEDURES,
        );
        const gExaminationsElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(
            DENTAL_EXAMINATIONS,
        );
        const gPastProceduresElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(
            DENTAL_PAST_PROCEDURES,
        );

        if (
            !gExaminationsFuse ||
            !gPastProceduresFuse ||
            !gExaminationsElements ||
            !gPastProceduresElements
        ) {
            return;
        }

        if (!input?.trim()) {
            let genieExaminationsOptions = gExaminationsElements.filter(
                Boolean,
            ) as GDentalExamination[];
            genieExaminationsOptions = genieExaminationsOptions.filter(
                (e) => !elements.find((t) => t.id === e.id),
            );
            genieExaminationsOptions.splice(7);
            genieExaminationsOptions = genieExaminationsOptions.map((i) => ({
                ...i,
                type: DENTAL_ENTITY_TYPE.EXAMINATION,
            }));
            let geniePastProceduresOptions = gPastProceduresElements.filter(
                Boolean,
            ) as GDentalPastProcedure[];
            geniePastProceduresOptions = geniePastProceduresOptions.filter(
                (e) => !elements.find((t) => t.id === e.id),
            );
            geniePastProceduresOptions.splice(7);
            geniePastProceduresOptions = geniePastProceduresOptions.map((i) => ({
                ...i,
                type: DENTAL_ENTITY_TYPE.PROCEDURE,
            }));
            if (genieExaminationsOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_FREQUENCTLY,
                    options: genieExaminationsOptions,
                    autosuggestHeading: 'examinations',
                });
            }
            if (geniePastProceduresOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_FREQUENCTLY,
                    options: geniePastProceduresOptions,
                    autosuggestHeading: 'procedures',
                });
            }
            setOptions(options, input);
        } else {
            let filteredGExaminationsItems = gExaminationsFuse
                .search(input)
                .map((option) => option.item)
                .filter(Boolean)
                .map((t) => ({ ...t, track: { source: 'GENIE' } })) as GDentalExamination[];
            filteredGExaminationsItems = filteredGExaminationsItems.filter(
                (e) => !elements.find((t) => t.id === e.id),
            );
            filteredGExaminationsItems.splice(7);
            filteredGExaminationsItems = filteredGExaminationsItems.map((i) => ({
                ...i,
                type: DENTAL_ENTITY_TYPE.EXAMINATION,
            }));
            let filteredGPastProceduresItems = gPastProceduresFuse
                .search(input)
                .map((option) => option.item)
                .filter(Boolean)
                .map((t) => ({ ...t, track: { source: 'GENIE' } })) as GDentalPastProcedure[];
            filteredGPastProceduresItems = filteredGPastProceduresItems.filter(
                (e) => !elements.find((t) => t.id === e.id),
            );
            filteredGPastProceduresItems.splice(7);
            filteredGPastProceduresItems = filteredGPastProceduresItems.map((i) => ({
                ...i,
                type: DENTAL_ENTITY_TYPE.PROCEDURE,
            }));
            if (filteredGExaminationsItems.length > 0) {
                options.push({
                    label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH,
                    options: filteredGExaminationsItems,
                    autosuggestHeading: 'examinations',
                    type: DENTAL_ENTITY_TYPE.EXAMINATION,
                });
            }
            if (filteredGPastProceduresItems.length > 0) {
                options.push({
                    label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH,
                    options: filteredGPastProceduresItems,
                    autosuggestHeading: 'procedures',
                    type: DENTAL_ENTITY_TYPE.PROCEDURE,
                });
            }

            setOptions(options, input);

            const fetchExaminations = fetchMatchingSymDia(input, undefined, 'dental');
            const fetchProcedures = fetchMatchingProcedures(input, 'dental');

            Promise.all([fetchExaminations, fetchProcedures])
                .then((values) => {
                    this.handleApiOptions(options, input, setOptions, values[0], values[1]);
                })
                .catch((err) => {
                    this.handleApiOptions(options, input, setOptions);
                });
        }
    };

    handleApiOptions = (
        currentOptions: AllOptions<DentalExamination>,
        input: string,
        setOptions: (options: AllOptions<DentalExamination>, inputValue?: string) => void,
        examinationsResponse?: SymDiaResponse,
        proceduresResponse?: ProcedureSuggestions[],
    ): void => {
        const { elements } = this.props;
        const options: AllOptions<DentalExamination> = [];

        let allExaminationsOptions =
            currentOptions.find((i) => i.type === DENTAL_ENTITY_TYPE.EXAMINATION)?.options || [];
        let allPastProceduresOptions =
            currentOptions.find((i) => i.type === DENTAL_ENTITY_TYPE.PROCEDURE)?.options || [];
        const examinationsOptions: SymDia[] = [];
        const proceduresOptions: ProcedureSuggestions[] = [];

        if (examinationsResponse?.symptoms) {
            examinationsOptions.push(...examinationsResponse.symptoms);
        }
        if (examinationsResponse?.diagnoses) {
            examinationsOptions.push(...examinationsResponse.diagnoses);
        }
        examinationsOptions?.filter(Boolean).forEach((i) => {
            if (!allExaminationsOptions.find((s) => s.id === i.id)) {
                allExaminationsOptions.push(i);
            }
        });
        if (proceduresResponse && proceduresResponse.length > 0) {
            proceduresOptions.push(...proceduresResponse);
        }
        proceduresOptions?.filter(Boolean).forEach((i) => {
            if (!allPastProceduresOptions.find((s) => s.id === i.id)) {
                allPastProceduresOptions.push(i);
            }
        });

        allExaminationsOptions = allExaminationsOptions.filter(
            (e) => !elements.find((t) => t.id === e.id),
        );
        allExaminationsOptions.splice(7);
        const isExaminationMatchFound = allExaminationsOptions.find(
            (i) => i.name.toLowerCase() === input.trim().toLowerCase(),
        );
        const isExaminationElementPresent = elements.find(
            (i) => i.name?.toLowerCase() === input?.trim().toLowerCase(),
        );
        if (!isExaminationMatchFound && input && !isExaminationElementPresent) {
            const id = `${LOCAL_ID_PREFIX}de-${new Date().getTime()}-${randomString(4)}`;
            allExaminationsOptions.push({
                id: id,
                name: input,
                entity_id: generateLocalIdForEntity(id),
            });
        }
        allExaminationsOptions = allExaminationsOptions.map((i) => ({
            ...i,
            type: DENTAL_ENTITY_TYPE.EXAMINATION,
        }));

        allPastProceduresOptions = allPastProceduresOptions.filter(
            (e) => !elements.find((t) => t.id === e.id),
        );
        allPastProceduresOptions.splice(7);
        const isPastProcedureMatchFound = allPastProceduresOptions.find(
            (i) => i.name.toLowerCase() === input.trim().toLowerCase(),
        );
        const isPastProcedureElementPresent = elements.find(
            (i) => i.name?.toLowerCase() === input?.trim().toLowerCase(),
        );
        if (!isPastProcedureMatchFound && input && !isPastProcedureElementPresent) {
            const id = `${LOCAL_ID_PREFIX}de-${new Date().getTime()}-${randomString(4)}`;
            allPastProceduresOptions.push({
                id: id,
                name: input,
                entity_id: generateLocalIdForEntity(id),
            });
        }
        allPastProceduresOptions = allPastProceduresOptions.map((i) => ({
            ...i,
            type: DENTAL_ENTITY_TYPE.PROCEDURE,
        }));

        if (allExaminationsOptions.length > 0) {
            options.push({
                label: AS_OPTIONS_SEARCH,
                options: allExaminationsOptions,
                autosuggestHeading: 'examinations',
            });
        }
        if (allPastProceduresOptions.length > 0) {
            options.push({
                label: AS_OPTIONS_SEARCH,
                options: allPastProceduresOptions,
                autosuggestHeading: 'procedures',
            });
        }
        setOptions(options, input);
    };

    OptionIcon = DentalExaminationIcon('w-12 h-12 fill-darwin-green-primary');

    render(): ReactNode {
        const { elements, onSelect, teeth, group } = this.props;
        return (
            <PadV2AutoSuggest
                inputId={`${DENTAL_EXAMINATION_AS_INPUT}-${group?.id || teeth}`}
                elId={`${DENTAL_EXAMINATION_AS_EL_ID}-${group?.id || teeth}`}
                onSelect={onSelect}
                optionIconText={this.OptionIcon}
                optionIconBgClass="circle-inventory-in-stock"
                menuListClassName="mx-dropdown"
                optionWrapperClass="mx-dropdown-item"
                getOptions={this.getOptions}
                placeholder="Start typing a examination or past procedure..."
                elements={elements}
                hideKeyboardShortCutsInfo
                customDataForAS={customDataForAS}
            />
        );
    }
}

export function toPxDentalExaminationFromApiObj(
    option: DentalExamination,
    teeth: string,
    surfaces: Surface[],
    group_id?: string,
): DentalExamination {
    return {
        id: option.id,
        name: option.name,
        teeth_id: teeth,
        entity_id: generateLocalIdForEntity(option.id),
        surfaces: surfaces,
        group_id: group_id,
        type: option.type,
    };
}

export const DentalExaminationOnPadAutoSuggest: React.FC<{
    teeth: string;
    surfaces: Surface[];
    group?: Group;
}> = ({ teeth, surfaces, group }) => {
    const dispatch = useDispatch();
    const elements = useSelector(getDentalExaminations);

    const onSelect = (option: SymDia) => {
        if (group) {
            const { id, data } = group;
            const groupData = Object.entries(data);
            batch(() => {
                groupData.forEach((i, index) => {
                    const surfacesArray = Array.from(i[1]).map((id) => ({
                        id,
                        name: allSurfacesIdToName[id],
                    }));
                    dispatch(
                        upsertDentalExamination(
                            toPxDentalExaminationFromApiObj(option, i[0], surfacesArray, id),
                            elements.length + index,
                            true,
                        ),
                    );
                });
            });
        } else {
            dispatch(
                upsertDentalExamination(
                    toPxDentalExaminationFromApiObj(option, teeth, surfaces),
                    elements.length,
                ),
            );
        }
        setTimeout(() => {
            const t = document.getElementById(
                `${DENTAL_EXAMINATION_AS_INPUT}-${group?.id || teeth}`,
            ) as HTMLInputElement;
            t && t.focus();
        }, 500);
    };
    let currentElements: DentalExamination[] = [];
    if (group) {
        currentElements = elements.filter(
            (i) => i.group_id === group.id && i.teeth_id === Object.keys(group.data)[0],
        );
    } else {
        currentElements = elements.filter((i) => i.teeth_id === teeth && !i.group_id);
    }
    return (
        <DentalExaminationAutoSuggest
            onSelect={onSelect}
            elements={currentElements}
            teeth={teeth}
            group={group}
        />
    );
};
